import { bundleTargetOf } from "@/libs/product/bundle-base";
import { camoBaseOf, isCamoSlug } from "@/libs/product/camo-variant";
import { SET_PACK_QUERY, setBaseOf } from "@/libs/product/set-variant";

/**
 * 제품 카드에서 "제품 상세" 로 보낼 URL (#5276).
 *
 * ## 왜 `product.hasPage` 로 분기하지 않는가
 *
 * API 의 `hasPage` 는 **라우팅 근거로 못 쓴다** — 실재하는 라우트와 양방향으로 어긋나 있다.
 * 2026-08-19 카탈로그 33개 전수 실측(`tactsuit-pro` PDP 의 `products`), 불일치 **7건**:
 *
 * | 방향 | 제품 | 종전 증상 |
 * | --- | --- | --- |
 * | 페이지 없는데 `true` | `original-badge-for-tactsuit-air` · `original-camo-badge-for-tactsuit-air` · `10th-birthday-badge-for-tactsuit-air` · `tactsuit-x-audio-accessories` | `/tactsuit/{slug}` → **404** (신고 내용) |
 * | 페이지 있는데 `false` | `fps-bundle` · `fps-bundle-tactvisor` · `starter-bundle` | 링크가 안 걸리고 **이미지 클릭이 담기**로 빠졌다 |
 *
 * `hasPage` 는 admin 입력값이라 라우트 추가/삭제와 동기화되지 않는다. 그래서 이 값으로
 * 목적지를 고르는 대신 **모든 제품을 PDP 로 보낸다**.
 *
 * ## 왜 PDP 하나로 통일해도 되는가
 *
 * `/shop/buy-tactsuit/{slug}` 는 33개 전 제품에서 404/500 이 **0건**이다. PDP 가
 * `src/pages/shop/buy-tactsuit/[slug].tsx` 의 `getServerSideProps` 에 정규화 리다이렉트를
 * 갖고 있어서, 전용 페이지가 없는 slug 도 알맞은 표면으로 흘러간다(실측):
 *
 * - `metro-awakening-bundle` → `…/tactsuit-pro?bundle=metro-awakening-bundle`
 * - `starter-bundle-camo` → `…/tactsuit-air?bundle=starter-bundle-camo&color=camo`
 * - `tactsuit-air-camo` → `…/tactsuit-air?color=camo`
 *
 * 반대 방향(모르면 `/tactsuit/`)은 라우트가 없으면 404 라 안전한 기본값이 될 수 없다.
 *
 * ## 알고 받아들인 것
 *
 * `/tactsuit/{slug}` 소개(마케팅) 페이지가 있는 제품도 이제 PDP 로 간다. 즉 `ProductCard` 로
 * 디바이스를 그리는 화면(카트 추천 · About · 게임 상세)에서 이미지 클릭 목적지가 소개 →
 * 구매 페이지로 바뀐다. 대신 slug 별 예외 목록과 그 목록이 실재 라우트와 어긋나는지 감시하는
 * 가드가 사라진다 — #5276 이 바로 그 종류의 목록(admin `hasPage`)이 어긋나서 난 사고다.
 * (소개 페이지 링크가 필요한 자리는 GNB 처럼 이미 명시적으로 걸려 있다.)
 *
 * ## 이 헬퍼를 쓰는 곳 (= `/tactsuit/{slug}` 직접 조립을 걷어낸 곳)
 *
 * 제품 상세로 보내는 표면은 **전부 여기를 지난다.** 새 표면을 만들 때 직접 조립하지 말 것.
 *
 * - `src/v2-components/ProductCard.tsx` — 제품 카드(PDP 액세서리 그리드 · 목록 · About 등)
 * - `src/v2-components/cart-sheet/cart-item.tsx` — 카트 라인 썸네일·제품명
 * - `src/v2-components/cart-sheet/product-card.tsx` — `/cart` 하단 추천 레일 2종
 * - `src/components/tactsuit/components/DisplayCategorizedBundles.tsx` — 번들 구성품 썸네일
 *
 * 세 곳은 게이트(`hasPage` / `productType !== ACCESSORY`)로 클릭을 막아 **상세로 갈 방법이
 * 아예 없던** 상태였다 — 404 가 아니라 "아무 일도 안 일어남" 이라 신고에 안 잡히고 오래
 * 남는다. 게이트를 없앨 때는 **목적지를 먼저 고칠 것**: 막혀 있던 항목이 통과하는 순간
 * 직접 조립이 그대로 404 가 된다(~~`product.tsx`~~ 가 정확히 그 상황이었다).
 *
 * ## 이 헬퍼를 쓰지 않는 곳
 *
 * 소개 페이지로 **일부러** 보내는 자리는 그대로 둔다(GNB · `KeyProducts` 등,
 * `/tactsuit/{slug}` 직접 조립 약 12곳 — 그때 함께 센 `TactsuitSubMenu` ·
 * `SpecificationProductCard` 는 렌더 경로가 없는 고아라 2026-10-01 에 지웠다).
 *
 * ⚠️ 그중 라우트 보장을 **실제로 확인한 건 `KeyProducts` 하나뿐**이다(디바이스 3종 slug
 * 하드코딩). 나머지는 이 PR 에서 감사하지 않았다 — 대상이 카탈로그에서 오는 곳이라면 같은
 * 404 를 낼 수 있다. 그 자리를 손댈 때 이 헬퍼로 옮길지 함께 판단할 것.
 */

/**
 * 제품 상세 페이지의 **경로부**. 쿼리를 붙이지 않는다.
 *
 * ★**판정·비교 전용이다** (`docs/product-variant-line-policy.md` §1 「판정과 표시를
 * 분리한다」). camo 만 해소한다 — camo 변형은 별도 제품이지만 URL 이 베이스로 통합돼
 * 있어서, 경로를 **대조**하려면 라인 대표로 환산해야 하기 때문이다.
 *
 * ⚠️ **번들은 여기서 해소하지 않는다.** 번들은 베이스 경로 + `?bundle=` 로 이관되는데,
 * 여기서 베이스로 환산하면 `fps-bundle` 이 **맨 `tactsuit-pro` PDP 에서 활성**으로 잡힌다
 * (쿼리를 안 보므로 `?bundle=` 유무를 못 가른다). 즉 해소 범위가 넓어지는 것이 곧 오탐이다.
 * 링크 목적지는 아래 `getProductDetailHref` 가 따로 해소한다.
 */
export const productDetailPathOf = (slug: string): string =>
  `/shop/buy-tactsuit/${camoBaseOf(slug)}`;

/**
 * 제품 상세(구매) 페이지로 보낼 **링크 목적지**.
 *
 * ★**리다이렉트를 타지 않는 최종 주소를 낸다** (#5798). 번들·camo 슬러그는 PDP 가
 * `getServerSideProps` 에서 **301** 로 「베이스 + 쿼리」로 이관한다. 종전엔 이 헬퍼가
 * 원본 slug 를 그대로 돌려줘서, 링크를 밟을 때마다 그 홉을 다시 썼다.
 *
 * prod 실측(2026-09-18 · `/ko/shop/buy-tactsuit/<slug>/`):
 *
 * ```
 * tactsuit-air-camo                      홉 1 → …/tactsuit-air/?color=camo
 * fps-bundle                             홉 2 → …/tactsuit-pro/?bundle=fps-bundle
 * starter-bundle-camo                    홉 2 → …/tactsuit-air/?bundle=starter-bundle-camo&color=camo
 * original-camo-badge-for-tactsuit-air   홉 0   ← 대조군. 이름에 camo 가 있어도 변형이 아니다
 * tactsuit-pro                           홉 0   ← 대조군
 * ```
 *
 * ⚠️ **해소 순서가 있다 — 번들이 먼저다.** `starter-bundle-camo` 는 camo 맵의 **값**이면서
 * 번들 맵의 **키**다. camo 를 먼저 보면 `starter-bundle?color=camo` 가 되는데 그 경로도
 * 번들이라 **여전히 리다이렉트된다.** PDP 자신도 번들을 먼저 본다(`[slug].tsx`) — 같은
 * 순서를 유지해야 링크와 리다이렉트의 목적지가 갈리지 않는다.
 *
 * ⚠️ 슬래시로 끝내지 말 것 — `trailingSlash: true` 라 `<Link>` 가 붙여준다. 표기 축이
 * 셋이고 요구가 다르다는 것은 `docs/pitfalls.md` #60 에 있다.
 */
export function getProductDetailHref(slug: string): string {
  // 수량 세트는 **단품 PDP 에 세트를 고른 채로** 보낸다(#6008) — 세트 자체 PDP 는 단품으로 바꿀 선택지가 없다.
  // 선택을 주소에 싣는 이유(R1): 이 함수는 화면 링크뿐 아니라 GMC 피드 `<g:link>`·JSON-LD `Offer.url` 도 만든다.
  // 기본값(세트)에 기대면 두 피드 항목(세트·단품)이 같은 주소가 되고, 그 주소의 구조화 데이터는 한 가격뿐이다.
  const setBase = setBaseOf(slug);
  if (setBase) {
    const baseHref = getProductDetailHref(setBase);
    return `${baseHref}${baseHref.includes("?") ? "&" : "?"}${SET_PACK_QUERY}=set`;
  }
  const bundle = bundleTargetOf(slug);
  if (bundle) return `/shop/buy-tactsuit/${bundle.base}?${bundle.query}`;
  if (isCamoSlug(slug)) return `${productDetailPathOf(slug)}?color=camo`;
  return productDetailPathOf(slug);
}

/**
 * 앱 웹뷰 브리지(`openBrowserWebView({ url })`)용 상세 경로 — `getProductDetailHref` 와 **같은 목적지**를
 * 브리지 모양으로 낸다: 앞 `/` 없음(브리지가 `${domain}/${url}` 로 붙인다) · **경로부 끝 `/`**(`<Link>` 가 없어서
 * 슬래시가 빠지면 외부 브라우저가 308 을 한 홉 더 탄다 — `__tests__/webview/bridge-url-shape.test.ts`).
 *
 * ```
 *   tactsuit-pro          shop/buy-tactsuit/tactsuit-pro/
 *   fps-bundle            shop/buy-tactsuit/tactsuit-pro/?bundle=fps-bundle
 *   starter-bundle-camo   shop/buy-tactsuit/tactsuit-air/?bundle=starter-bundle-camo&color=camo
 * ```
 *
 * 종전 웹뷰 카드는 `tactsuit/<slug>/`(소개 페이지 리다이렉트)에 기대 `starter-bundle-camo` · `exoshock-bundle` 이
 * prod 404 였다(2026-11-03 실측, #6157 R1).
 */
export function getProductDetailBridgePath(slug: string): string {
  const [path, query] = getProductDetailHref(slug).split("?");
  const p = path.replace(/^\//, "");
  return `${p.endsWith("/") ? p : `${p}/`}${query ? `?${query}` : ""}`;
}

/**
 * 이 제품이 **지금 보고 있는 페이지**인가 (#5386).
 *
 * ## 왜 `router.pathname` 으로는 안 되나
 *
 * GNB 모바일 메뉴는 종전에 `router.pathname === item.href` 로 판정했다. PDP 는 **동적
 * 라우트**라 `pathname` 이 `/shop/buy-tactsuit/[slug]` 라는 **패턴 문자열**이고, `item.href`
 * (`/shop/buy-tactsuit/tactsuit-air`)와 **절대 같아지지 않는다.** 그래서
 *
 *   · 선택 표시(볼드)가 어떤 제품에서도 안 떴고
 *   · 그 조건 안에만 있던 `onClose()` 가 불리지 않아 **메뉴가 닫히지 않았다**
 *
 * 둘이 같은 뿌리였다. 그래서 판정을 `asPath` 기반으로 옮기고 여기 한 곳에 둔다.
 *
 * ## camo 는 경로만으로 못 가른다
 *
 * camo 는 별도 제품인데 URL 은 베이스 제품의 `?color=camo` 로 통합된다
 * (`camo-variant.ts` · `[slug].tsx` 의 정규화 리다이렉트). 즉 `tactsuit-air-camo` 를 눌러
 * 도착한 URL 은 `/shop/buy-tactsuit/tactsuit-air?color=camo` 다.
 *
 * 경로만 비교하면 **카모 페이지에서 `TactSuit Air` 가 볼드로 잡힌다**(엉뚱한 항목). 그래서
 * `color` 쿼리까지 본다 — 두 항목이 **서로 배타적**으로 판정된다:
 *
 * | 현재 URL | `tactsuit-air` | `tactsuit-air-camo` |
 * | --- | --- | --- |
 * | `…/tactsuit-air` | **활성** | 비활성 |
 * | `…/tactsuit-air?color=camo` | 비활성 | **활성** |
 *
 * ⚠️ `asPath` 를 넘길 것 — `pathname` 은 위 이유로 못 쓴다. `asPath` 는 locale 접두사를
 * 포함하지 않으므로(Next i18n) 로케일별 분기가 필요 없다.
 */
export function isProductDetailActive(asPath: string, slug: string): boolean {
  const [rawPath, rawQuery = ""] = asPath.split("?");
  // `trailingSlash: true` 라 도착 URL 은 슬래시로 끝난다. href 는 슬래시 없이 만든다.
  const path = rawPath.replace(/\/+$/, "");
  // ⚠️ **`getProductDetailHref` 를 쓰지 않는다** (#5798). 그쪽은 이제 번들·camo 를
  //    쿼리까지 붙여 해소하므로 경로 대조에 못 쓴다. 판정은 경로부만 본다.
  if (path !== productDetailPathOf(slug)) return false;

  const isCamoActive = new URLSearchParams(rawQuery).get("color") === "camo";
  return isCamoSlug(slug) ? isCamoActive : !isCamoActive;
}
